import { type CSSProperties, type MouseEvent } from 'react';

import type { ChipsSize, MultiChoiceChipsItem } from './chips.types.ts';
import { ChipsButton } from './ChipsButton.tsx';
import { ChipsGrid } from './multiChoiceChips/multiChoiceChips.style';

/** Updater form avoids stale closure when rapidly clicking; parent/store can apply to latest state. */
type MultiChoiceChipsChangePayload = string[] | ((prev: string[]) => string[]);

type MultiChoiceChipsProps<T = undefined> = {
  items: MultiChoiceChipsItem<T>[];
  value: string[];
  onChange: (selectedIds: MultiChoiceChipsChangePayload) => void;
  size?: ChipsSize;
  checkMark?: boolean;
  className?: string;
  style?: CSSProperties;
};

export function MultiChoiceChips<T = undefined>({
  items,
  value,
  onChange,
  size = 'medium',
  checkMark = false,
  className,
  style,
}: MultiChoiceChipsProps<T>) {
  const handleClick = (item: MultiChoiceChipsItem<T>) => (e: MouseEvent<HTMLButtonElement>) => {
    item.onClick?.(e);
    if (item.onClick) return;
    const { id } = item;
    // Updater form: only id in closure; parent applies to latest state (fixes rapid-click race).
    onChange(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  };

  return (
    <ChipsGrid className={className} style={style}>
      {items.map(item => {
        const { selectedWhen, selected: itemSelected, ...itemRest } = item;
        const selected = selectedWhen
          ? selectedWhen(value)
          : typeof itemSelected === 'boolean'
            ? itemSelected
            : value.includes(item.id);
        return (
          <ChipsButton
            key={item.id}
            checkMark={checkMark}
            {...itemRest}
            size={size}
            selected={selected}
            onClick={handleClick(item)}
          />
        );
      })}
    </ChipsGrid>
  );
}
